import React, { useEffect, useRef, useMemo } from 'react';
import { useLanguage } from '../../../../../context/LanguageContext';
import { Credentials } from '../../../../../assets/features/Credentials';
import '../style/appointment-section.css';
import { initializeAppointmentSection } from '../script/appointment-section';
import { initializeTitleAnimation } from '../script/title-animation';
import appointmentImage from '../../../../../assets/logo/contact/malveira_img_form_430 x 460.webp';
import appointmentBgShape from '../../../../../assets/images/shapes/appointment-one-bg-shape.png';

const AppointmentSection: React.FC = () => {
    const { t, language } = useLanguage();
    const containerRef = useRef<HTMLElement>(null);
    const formRef = useRef<HTMLFormElement>(null);
    const serviceSelectRef = useRef<HTMLSelectElement>(null);

    // Access translations - recalculate when language changes to ensure re-render
    const translation = useMemo(() => t as any, [t, language]);
    const appointmentTranslations = useMemo(() => {
        const appt = translation.APPOINTMENT;
        return {
            getInTouchTagline: appt?.getInTouchTagline,
            contactUs: appt?.contactUs ,
            sendMessage: appt?.sendMessage ,
            fullName: appt?.fullName,
            emailAddress: appt?.emailAddress,
            phoneNumber: appt?.phoneNumber,
            selectService: appt?.selectService,
            yourMessage: appt?.yourMessage,
            privacyPolicyText: appt?.privacyPolicyText,
        };
    }, [translation, language]);
    const servicesTranslations = useMemo(() => translation.SERVICES, [translation, language]);

    // Service mapping
    const services = useMemo(() => [
        { value: 'general_mechanics', key: 'generalMechanics' },
        { value: 'air_conditioning', key: 'airConditioning' },
        { value: 'alignment', key: 'alignment' },
        { value: 'batteries', key: 'batteries' },
        { value: 'brakes', key: 'brakes' },
        { value: 'diagnostic_test', key: 'diagnosticTest' },
        { value: 'workshop_package', key: 'workshopServices' },
        { value: 'headlight_restoration', key: 'headlightRestoration' },
        { value: 'inspection', key: 'inspection' },
        { value: 'roady_review', key: 'roadyReview' },
        { value: 'shock_absorbers', key: 'shockAbsorbers' }
    ], []);

    useEffect(() => {
        if (!containerRef.current) {
            return;
        }

        const hasInitialized = sessionStorage.getItem('appointment-titles-initialized');
        
        // Initialize title animation
        const titleTimer = setTimeout(() => {
            if (!hasInitialized) {
            initializeTitleAnimation();
                sessionStorage.setItem('appointment-titles-initialized', 'true');
            } else {
                // On language change, just make titles visible without animation
                const titles = document.querySelectorAll('.appointment-one .title-animation');
                titles.forEach((title: Element) => {
                    const el = title as HTMLElement;
                    el.style.opacity = '1';
                });
            }
            
            // Refresh ScrollTrigger
            if (typeof window !== 'undefined' && (window as any).ScrollTrigger) {
                (window as any).ScrollTrigger.refresh();
            }
        }, 100);

        // Initialize appointment section (form validation, service select, etc.)
        const timer = setTimeout(() => {
            initializeAppointmentSection(containerRef.current, formRef.current, serviceSelectRef.current, services, servicesTranslations, language);
        }, 500);

        return () => {
            clearTimeout(timer);
            clearTimeout(titleTimer);
        };
    }, [language, servicesTranslations, services]); // Re-run when language or services change

    // Initialize reCAPTCHA after script loads
    useEffect(() => {
        const initRecaptcha = () => {
            const recaptchaElement = containerRef.current?.querySelector('#appointment-recaptcha') as HTMLElement;
            if (!recaptchaElement) return;

            // Check if reCAPTCHA script is loaded
            if (typeof (window as any).grecaptcha !== 'undefined' && (window as any).grecaptcha.render) {
                // Check if already rendered (has child elements from reCAPTCHA)
                if (recaptchaElement.children.length > 0) {
                    // Already rendered
                    return;
                }

                // Clear any existing content
                recaptchaElement.innerHTML = '';

                // Render reCAPTCHA
                try {
                    const widgetId = (window as any).grecaptcha.render(recaptchaElement, {
                        sitekey: Credentials.RECAPTCHA_SITE_KEY,
                        theme: 'light'
                    });
                    recaptchaElement.setAttribute('data-recaptcha-id', widgetId.toString());
                } catch (_) {
                }
            } else {
                // Script not loaded yet, retry
                setTimeout(initRecaptcha, 500);
            }
        };

        // Start initialization after a short delay to ensure DOM is ready
        const timer = setTimeout(initRecaptcha, 1000);

        return () => {
            clearTimeout(timer);
        };
    }, []);

    return (
        <section className="appointment-one" id="appointment-form" ref={containerRef}>
            <div className="container">
                <div className="appointment-one__inner">
                    <div className="appointment-one__bg-shape" style={{ backgroundImage: `url(${appointmentBgShape})` }}></div>
                    <div className="row">
                        <div className="col-xl-5 col-lg-4"></div>
                        <div className="col-xl-7 col-lg-8">
                            <div className="appointment-one__right">
                                <div className="section-title text-left sec-title-animation animation-style2">
                                    <div className="section-title__tagline-box">
                                        <div className="section-title__tagline-border">
                                            <div className="section-title__shape-1">
                                                <i className="section-title__circle"></i>
                                            </div>
                                        </div>
                                        <h6 className="section-title__tagline">
                                            {appointmentTranslations.getInTouchTagline}
                                        </h6>
                                        <div className="section-title__tagline-border">
                                            <div className="section-title__shape-2">
                                                <i className="section-title__circle"></i>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <form 
                                    className="contact-form-validated appointment-one__form"
                                    noValidate
                                    ref={formRef}
                                >
                                    <input type="hidden" name="language" id="appointment-language" value={language} />
                                    <div className="row">
                                        <div className="col-xl-6 col-lg-6 col-md-6">
                                            <div className="appointment-one__input-box">
                                                <input 
                                                    type="text" 
                                                    name="name" 
                                                    placeholder={appointmentTranslations.fullName} 
                                                    required 
                                                />
                                            </div>
                                        </div>
                                        <div className="col-xl-6 col-lg-6 col-md-6">
                                            <div className="appointment-one__input-box">
                                                <input 
                                                    type="email" 
                                                    name="email" 
                                                    placeholder={appointmentTranslations.emailAddress}
                                                    required 
                                                />
                                            </div>
                                        </div>
                                        <div className="col-xl-6 col-lg-6 col-md-6">
                                            <div className="appointment-one__input-box">
                                                <input 
                                                    type="text" 
                                                    name="Phone" 
                                                    placeholder={appointmentTranslations.phoneNumber} 
                                                    required 
                                                />
                                            </div>
                                        </div>
                                        <div className="col-xl-6 col-lg-6 col-md-6">
                                            <div className="appointment-one__input-box">
                                                <div className="select-box">
                                                    <select className="selectmenu wide" name="service" id="service-select" ref={serviceSelectRef} defaultValue="">
                                                        <option value="">
                                                            {appointmentTranslations.selectService}
                                                        </option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-xl-12">
                                            <div className="appointment-one__input-box">
                                                <textarea 
                                                    name="message" 
                                                    placeholder={appointmentTranslations.yourMessage} 
                                                    rows={4} 
                                                    required
                                                ></textarea>
                                            </div>
                                        </div>
                                        <div className="col-xl-12">
                                            <div className="appointment-one__checkbox-box">
                                                <div className="appointment-one__checkbox">
                                                    <input type="checkbox" id="privacy_policy_contact" name="privacy_policy" required />
                                                    <label htmlFor="privacy_policy_contact" id="privacy_policy_contact_label">
                                                        <span className="appointment-one__checkbox-mark"></span>
                                                        <span 
                                                            className="appointment-one__checkbox-text"
                                                            dangerouslySetInnerHTML={{ 
                                                                __html: appointmentTranslations.privacyPolicyText
                                                            }}
                                                        ></span>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-xl-12">
                                            <div className="appointment-one__recaptcha-btn-row">
                                                <div className="appointment-one__recaptcha-box">
                                                    <div className="g-recaptcha" id="appointment-recaptcha"></div>
                                                </div>
                                                <div className="appointment-one__btn-box">
                                                    <button type="submit" className="thm-btn appointment-now-btn contact-us-btn">
                                                        {appointmentTranslations.sendMessage}
                                                        <span className="btn-arrow"><i className="icon-next"></i></span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                                <div className="result"></div>
                            </div>
                        </div>
                    </div>
                    <div className="appointment-one__img">
                        <img loading="lazy" src={appointmentImage} alt="" className="appointment-one__img-element" />
                    </div>
                </div>
            </div>
        </section>
    );
};

export default AppointmentSection;

